import ApiService from "@/services/api.service";

// ACTIONS
export const LIST_SERVIZI = "listServizi";
export const GET_SERVIZIO = "getServizio";
export const CREATE_SERVIZIO = "createServizio";
export const UPDATE_SERVIZIO = "updateServizio";
export const DISATTIVA_SERVIZIO = "disattivaServizio";

// MUTATIONS
export const SET_LIST_SERVIZI = "setListServizi";
export const SET_LOADING = "setLoading";
export const SET_ERROR = "setError";
export const SET_ERRORE_CARICAMENTO = "setErroreCaricamento";

// Controller di backend: [Route("api/[controller]")] -> /api/Servizio.
// La baseURL di axios è VUE_APP_ROOT_API (default "/api").
const RESOURCE = "Servizio";

// Testo usato quando l'errore non contiene un messaggio leggibile.
const MESSAGGIO_ERRORE_GENERICO =
    "Si è verificato un errore, riprova più tardi.";

// Il backend risponde agli errori con HTTP 500 e body {Status, Message}
// (Response<object> di questa codebase). A seconda della configurazione JSON il
// campo arriva come "message" o "Message": si accettano entrambe le forme, più le
// varianti ProblemDetails, e in ogni caso la mutation riceve una stringa.
const toMessage = (err) => {
    if (typeof err === "string") {
        return err.trim() || MESSAGGIO_ERRORE_GENERICO;
    }
    if (!err) {
        return MESSAGGIO_ERRORE_GENERICO;
    }

    const candidati = [
        err.response?.data?.message,
        err.response?.data?.Message,
        err.response?.data?.detail,
        err.response?.data?.title,
        err.response?.data,
        err.data?.message,
        err.data?.Message,
        err.detail,
        err.title,
        err.message,
    ];
    const messaggio = candidati.find(
        (valore) => typeof valore === "string" && valore.trim() !== ""
    );
    if (messaggio) {
        return messaggio.trim();
    }

    try {
        const serializzato = JSON.stringify(err);
        if (serializzato && serializzato !== "{}" && serializzato !== "[]") {
            return serializzato;
        }
    } catch {
        // Oggetto non serializzabile: resta il messaggio generico.
    }

    return MESSAGGIO_ERRORE_GENERICO;
};

// GET /api/Servizio?attivo=&categoria=&search=
// I tre parametri sono opzionali: un filtro vuoto non viene inviato, perché
// "parametro assente" significa "nessun filtro" (search stringa vuota non filtra).
const buildQuery = (filters = {}) => {
    const { attivo, categoria, search } = filters;
    const params = new URLSearchParams();

    if (attivo === true || attivo === false) {
        params.append("attivo", String(attivo));
    }
    if (categoria) {
        params.append("categoria", categoria);
    }
    if (search) {
        params.append("search", search);
    }

    const qs = params.toString();
    return qs ? `?${qs}` : "";
};

const state = {
    list: [],
    loading: false,
    error: {},
    // Errore del caricamento della lista: stringa (messaggio) oppure null.
    // Distingue l'elenco realmente vuoto dal caricamento fallito.
    erroreCaricamento: null,
};

const getters = {
    getList: (state) => state.list,
    getLoading: (state) => state.loading,
    getError: (state) => {
        if (typeof state.error === "string") {
            return state.error;
        }
        if (!state.error || !Object.keys(state.error).length) {
            return null;
        }
        return toMessage(state.error);
    },
    getErroreCaricamento: (state) => state.erroreCaricamento,
};

const actions = {
    // Elenco servizi. Endpoint: GET /api/Servizio?attivo=&categoria=&search=
    // Il backend restituisce l'intero array filtrato: nessun contratto di
    // paginazione, quindi la tabella pagina lato client.
    [LIST_SERVIZI](context, filters) {
        context.commit(SET_LOADING, true);
        context.commit(SET_ERRORE_CARICAMENTO, null);

        return new Promise((resolve) => {
            ApiService.get(`${RESOURCE}${buildQuery(filters)}`)
                .then(({ data }) => {
                    context.commit(SET_LIST_SERVIZI, data);
                    context.commit(SET_ERRORE_CARICAMENTO, null);
                    resolve(data);
                })
                .catch((error) => {
                    // Svuota l'elenco committato: senza questo la tabella
                    // mostrerebbe le righe del filtro precedente mentre l'alert
                    // dice che il caricamento è fallito.
                    context.commit(SET_LIST_SERVIZI, []);
                    const messaggio = toMessage(error);
                    context.commit(SET_ERROR, messaggio);
                    context.commit(SET_ERRORE_CARICAMENTO, messaggio);
                    setTimeout(() => context.commit(SET_ERROR, ""), 5000);
                    resolve([]);
                })
                .finally(() => context.commit(SET_LOADING, false));
        });
    },

    // Lettura di un singolo servizio. Endpoint: GET /api/Servizio/{codice}
    // Il contratto prevede 200 con l'oggetto oppure 200 con body null se il
    // codice non esiste: in quel caso il chiamante riceve null (nessuna eccezione).
    [GET_SERVIZIO](context, codice) {
        return new Promise((resolve) => {
            ApiService.download(RESOURCE, encodeURIComponent(codice))
                .then(({ data }) => {
                    resolve(data);
                })
                .catch((error) => {
                    context.commit(SET_ERROR, toMessage(error));
                    setTimeout(() => context.commit(SET_ERROR, ""), 5000);
                    resolve(null);
                });
        });
    },

    // Creazione. Endpoint: POST /api/Servizio
    // Body: AddServizioRequest = tutti i campi del DTO tranne Id e Attivo
    // (il backend forza Attivo = true alla creazione e valida codice/descrizione).
    [CREATE_SERVIZIO](context, params) {
        return new Promise((resolve, reject) => {
            ApiService.post(RESOURCE, params)
                .then(({ data }) => {
                    resolve(data);
                })
                .catch((error) => {
                    context.commit(SET_ERROR, toMessage(error));
                    setTimeout(() => context.commit(SET_ERROR, ""), 5000);
                    reject(error);
                });
        });
    },

    // Aggiornamento. Endpoint: PUT /api/Servizio/{codice}
    // Body: UpdateServizioRequest = tutti i campi del DTO tranne Id e Codice
    // (il codice identifica la riga e viaggia solo nella rotta: non è modificabile).
    [UPDATE_SERVIZIO](context, params) {
        return new Promise((resolve, reject) => {
            const { codice, ...body } = params;
            ApiService.update(RESOURCE, encodeURIComponent(codice), body)
                .then(({ data }) => {
                    resolve(data);
                })
                .catch((error) => {
                    context.commit(SET_ERROR, toMessage(error));
                    setTimeout(() => context.commit(SET_ERROR, ""), 5000);
                    reject(error);
                });
        });
    },

    // Disattivazione. Endpoint: DELETE /api/Servizio/{codice}
    // Non è una cancellazione: il backend imposta attivo = false e la riga resta
    // in anagrafica (i listini che la referenziano continuano a funzionare).
    [DISATTIVA_SERVIZIO](context, codice) {
        return new Promise((resolve, reject) => {
            ApiService.delete(RESOURCE, encodeURIComponent(codice))
                .then(({ data }) => {
                    resolve(data);
                })
                .catch((error) => {
                    context.commit(SET_ERROR, toMessage(error));
                    setTimeout(() => context.commit(SET_ERROR, ""), 5000);
                    reject(error);
                });
        });
    },
};

const mutations = {
    [SET_LIST_SERVIZI](state, list) {
        state.list = Array.isArray(list) ? list : [];
    },
    [SET_LOADING](state, loading) {
        state.loading = loading;
    },
    [SET_ERROR](state, error) {
        state.error = error;
    },
    // Sempre null oppure stringa: distingue l'elenco vuoto dal caricamento fallito.
    [SET_ERRORE_CARICAMENTO](state, valore) {
        state.erroreCaricamento =
            typeof valore === "string" && valore !== "" ? valore : null;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
